import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import NativePushBootstrap from "@/components/NativePushBootstrap";
import { Toaster } from "@/components/ui/sonner";
import TopProgressBar from "@/components/TopProgressBar";
import InstallPrompt from "@/components/InstallPrompt";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Dogs from "@/pages/Dogs";
import DogForm from "@/pages/DogForm";
import CarePassport from "@/pages/CarePassport";
import DogDetail from "@/pages/DogDetail";
import Litters from "@/pages/Litters";
import LitterForm from "@/pages/LitterForm";
import UpcomingLitterNew from "@/pages/UpcomingLitterNew";
import UpcomingLitterDetail from "@/pages/UpcomingLitterDetail";
import MarketplaceUpcoming from "@/pages/MarketplaceUpcoming";
import MarketplaceUpcomingDetail from "@/pages/MarketplaceUpcomingDetail";
import Breeders from "@/pages/Breeders";
import BreederDetail from "@/pages/BreederDetail";
import MyKennelRedirect from "@/pages/MyKennelRedirect";
import Pedigree from "@/pages/Pedigree";
import PdfViewer from "@/pages/PdfViewer";
import Billing from "@/pages/Billing";
import BillingReturn from "@/pages/BillingReturn";
import Services from "@/pages/Services";
import AdminPayments from "@/pages/AdminPayments";
import AdminAccounting from "@/pages/AdminAccounting";
import AdminDocuments from "@/pages/AdminDocuments";
import AdminReset from "@/pages/AdminReset";
import AdminDuplicateDogs from "@/pages/AdminDuplicateDogs";
import AdminPushTest from "@/pages/AdminPushTest";
import PublicPost from "@/pages/PublicPost";
import AdminAutomations from "@/pages/AdminAutomations";
import AdminReferrals from "@/pages/AdminReferrals";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import AdminEvents from "@/pages/AdminEvents";
import Inbox from "@/pages/Inbox";
import PublicBreeder from "@/pages/PublicBreeder";
import PublicDog from "@/pages/PublicDog";
import MyCertificates from "@/pages/MyCertificates";
import Breeding from "@/pages/Breeding";
import MyBreedingListings from "@/pages/MyBreedingListings";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import About from "@/pages/About";
import PressKit from "@/pages/PressKit";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminCompList from "@/pages/AdminCompList";
import AdminDocViewer from "@/pages/AdminDocViewer";
import AdminServiceProviders from "@/pages/AdminServiceProviders";
import ProviderOnboarding from "@/pages/ProviderOnboarding";
import FindPros from "@/pages/FindPros";
import PublicProvider from "@/pages/PublicProvider";
import MyBookings from "@/pages/MyBookings";
import Transfers from "@/pages/Transfers";
import AdminTransfers from "@/pages/AdminTransfers";
import AdminPedigreeAdditions from "@/pages/AdminPedigreeAdditions";
import AdminMasterPedigree from "@/pages/AdminMasterPedigree";
import AdminData from "@/pages/AdminData";
import AdminAboutPhotos from "@/pages/AdminAboutPhotos";
import LitterConfirmation from "@/pages/LitterConfirmation";
import Marketplace from "@/pages/Marketplace";
import WtbBoard from "@/pages/WtbBoard";
import WtbDetail from "@/pages/WtbDetail";
import IntlBreeders from "@/pages/IntlBreeders";
import IntlBreederApply from "@/pages/IntlBreederApply";
import IntlBreederDetail from "@/pages/IntlBreederDetail";
import ImportBreedFromCountry from "@/pages/ImportBreedFromCountry";
import WtbByBreed from "@/pages/WtbByBreed";
import GiftPage, { GiftSentPage } from "@/pages/GiftPage";
import GiftRedeem from "@/pages/GiftRedeem";
import PricingSpecialty from "@/pages/PricingSpecialty";
import AcceptInquiry from "@/pages/AcceptInquiry";
import BuyerActivityFeed from "@/pages/BuyerActivityFeed";
import PublicCatalog from "@/pages/PublicCatalog";
import Calendar from "@/pages/Calendar";
import AdminPitchKit from "@/pages/AdminPitchKit";
import BreedsCatalog from "@/pages/BreedsCatalog";
import AdminBreedsCatalog from "@/pages/AdminBreedsCatalog";
import AdminApprovals from "@/pages/AdminApprovals";
import AdminReports from "@/pages/AdminReports";
import AdminDNAQueue from "@/pages/AdminDNAQueue";
import WhyDNAVerified from "@/pages/WhyDNAVerified";
import Payouts from "@/pages/Payouts";
import MyEscrow from "@/pages/MyEscrow";
import EscrowReturn from "@/pages/EscrowReturn";
import AdminEscrow from "@/pages/AdminEscrow";
import ImportDogs from "@/pages/ImportDogs";
import BreederAnalytics from "@/pages/BreederAnalytics";
import PublicKennels from "@/pages/PublicKennels";
import VerifiedSeal from "@/pages/VerifiedSeal";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import AdminJournal from "@/pages/AdminJournal";
import AdminJournalSettings from "@/pages/AdminJournalSettings";
import AdminPuppySchool from "@/pages/AdminPuppySchool";
import AdminIPPortfolio from "@/pages/AdminIPPortfolio";
import MarketingReelBuyer from "@/pages/marketing/MarketingReelBuyer";
import MarketingReelSeller from "@/pages/marketing/MarketingReelSeller";

import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import WorldwideLegalDrafts from "@/pages/legal/WorldwideLegalDrafts";
import StorefrontSellerAgreement from "@/pages/legal/StorefrontSellerAgreement";
import UserConsent from "@/pages/legal/UserConsent";
import EthicalConsiderations from "@/pages/legal/EthicalConsiderations";
import AccessibilityStatement from "@/pages/legal/AccessibilityStatement";
import SecurityMeasures from "@/pages/legal/SecurityMeasures";
import ComplianceDisclosures from "@/pages/legal/ComplianceDisclosures";
import Contact from "@/pages/legal/Contact";
import TransferPay from "@/pages/TransferPay";
import Feed from "@/pages/Feed";

import ReferralCapture from "@/components/ReferralCapture";
import CookieConsent from "@/components/CookieConsent";
import OfflineBanner from "@/components/OfflineBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import NotFound from "@/pages/NotFound";
import StatusPage from "@/pages/StatusPage";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import SubProcessors from "@/pages/legal/SubProcessors";
import CookieNotice from "@/pages/legal/CookieNotice";
import AIUseDisclosure from "@/pages/legal/AIUseDisclosure";
import AutoRenewDisclosure from "@/pages/legal/AutoRenewDisclosure";
import PhotoContentLicense from "@/pages/legal/PhotoContentLicense";
import Features from "@/pages/Features";
import OnePagers from "@/pages/OnePagers";
import Shop from "@/pages/Shop";
import PuppiesByBreedState from "@/pages/PuppiesByBreedState";
import ClearedProsByState from "@/pages/ClearedProsByState";
import SavedSearches from "@/pages/SavedSearches";
import SellerStandards from "@/pages/SellerStandards";
import SellerStandardsAttest from "@/pages/SellerStandardsAttest";
import AdminSellerStandards from "@/pages/AdminSellerStandards";
import BuyerRights from "@/pages/BuyerRights";
import AdminBuyerRights from "@/pages/AdminBuyerRights";
import AdminLegalBundle from "@/pages/AdminLegalBundle";
import AdminLaunchReadiness from "@/pages/AdminLaunchReadiness";
import AdminStripeRadar from "@/pages/AdminStripeRadar";
import AdminStripePivotPlaybook from "@/pages/AdminStripePivotPlaybook";
import SitterView from "@/pages/SitterView";
import DogLogLanding from "@/pages/DogLogLanding";
import DogLogHub from "@/pages/DogLogHub";
import Armstrong from "@/pages/Armstrong";
import AdminArmstrong from "@/pages/AdminArmstrong";
import ArmstrongCustomOrder from "@/pages/ArmstrongCustomOrder";
import StoreCustomOrder from "@/pages/StoreCustomOrder";
import AdminStorefronts from "@/pages/AdminStorefronts";
import MyStore from "@/pages/MyStore";
import PublicStore from "@/pages/PublicStore";
import PublicStorefronts from "@/pages/PublicStorefronts";
import AdminRoadmap from "@/pages/AdminRoadmap";
import MyVideoTours from "@/pages/MyVideoTours";
import VideoTourRoom from "@/pages/VideoTourRoom";
import VideoToursLanding from "@/pages/VideoToursLanding";
import VerifyReference from "@/pages/VerifyReference";
import AdminLaunchPack from "@/pages/AdminLaunchPack";
import AdminLaunchPlaybook from "@/pages/AdminLaunchPlaybook";
import AdminMobileLaunch from "@/pages/AdminMobileLaunch";
import AccountSubscription from "@/pages/AccountSubscription";
import AdminPaywallAudit from "@/pages/AdminPaywallAudit";
import AdminAnnouncements from "@/pages/AdminAnnouncements";
import AdminConnectDiagnose from "@/pages/AdminConnectDiagnose";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminMessaging from "@/pages/AdminMessaging";
import AdminMedia from "@/pages/AdminMedia";
import MessagesPage from "@/pages/MessagesPage";
import ThreadView from "@/pages/ThreadView";
import MediaLibrary from "@/pages/MediaLibrary";
import MediaItem from "@/pages/MediaItem";
import AdminConsole from "@/pages/AdminConsole";
import AdminOperations from "@/pages/AdminOperations";
import AdminCompliance from "@/pages/AdminCompliance";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminBusinessClassification from "@/pages/AdminBusinessClassification";
import AdminOperatorBinder from "@/pages/AdminOperatorBinder";
import AdminContracts from "@/pages/AdminContracts";
import PrivacyDataPage from "@/pages/PrivacyDataPage";
import BulkImportDogs from "@/pages/BulkImportDogs";
import ShareKennelPage from "@/pages/ShareKennelPage";
import ContractsHub from "@/pages/ContractsHub";
import AdminDogReassign from "@/pages/AdminDogReassign";
import AdminStorefrontReassign from "@/pages/AdminStorefrontReassign";
import AdminArmstrongMigrate from "@/pages/AdminArmstrongMigrate";
import AdminStorefrontsHealth from "@/pages/AdminStorefrontsHealth";
import Pricing from "@/pages/Pricing";
import AdminTransporters from "@/pages/AdminTransporters";
import AdminTransportDisputes from "@/pages/AdminTransportDisputes";
import AdminPersonas from "@/pages/AdminPersonas";
import AdminPricing from "@/pages/AdminPricing";
import AdminPricingReference from "@/pages/AdminPricingReference";
import AdminIntlOps from "@/pages/AdminIntlOps";
import AdminIntlCouriers from "@/pages/AdminIntlCouriers";
import AdminChatReports from "@/pages/AdminChatReports";
import AdminUsersOps from "@/pages/AdminUsersOps";
import AdminShareInvite from "@/pages/AdminShareInvite";
import InviteFriends from "@/pages/InviteFriends";
import Messages from "@/pages/Messages";
import AdminIntlSellers from "@/pages/AdminIntlSellers";
import AdminFeedPrompts from "@/pages/AdminFeedPrompts";
import TransportRoutePage from "@/pages/TransportRoutePage";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminInvites from "@/pages/AdminInvites";
import AdminMarketingRefresh from "@/pages/AdminMarketingRefresh";
import AdminSocialBios from "@/pages/AdminSocialBios";
import AdminCheckrSetup from "@/pages/AdminCheckrSetup";
import AdminStripeTaxSetup from "@/pages/AdminStripeTaxSetup";
import StripeTaxSetupHelp from "@/pages/StripeTaxSetupHelp";
import HelpTaxes1099k from "@/pages/HelpTaxes1099k";
import StripeConnectReturn from "@/pages/StripeConnectReturn";
import Install from "@/pages/Install";
import TransportRequest from "@/pages/TransportRequest";
import TransportApply from "@/pages/TransportApply";
import TransportDirectory from "@/pages/TransportDirectory";
import InternationalTransportDirectory from "@/pages/InternationalTransportDirectory";
import InternationalTransportApply from "@/pages/InternationalTransportApply";
import InternationalTransporterProfile from "@/pages/InternationalTransporterProfile";
import InternationalTransportApplyThanks from "@/pages/InternationalTransportApplyThanks";
import InternationalServicesDirectory from "@/pages/InternationalServicesDirectory";
import InternationalServiceProApply from "@/pages/InternationalServiceProApply";
import InternationalServiceProProfile from "@/pages/InternationalServiceProProfile";
import TripPublic from "@/pages/TripPublic";
import TransportProfile from "@/pages/TransportProfile";
import TransportGettingStarted from "@/pages/TransportGettingStarted";
import TransportOrderDetail from "@/pages/TransportOrderDetail";
import TransporterDashboard from "@/pages/TransporterDashboard";
import AdminAnalytics from "@/pages/AdminAnalytics";
import { initSentry } from "@/lib/sentry";
import ReferralProgram from "@/pages/ReferralProgram";
// ChatPage retired 2026-06 — /chat now redirects to /messages (Registry Chat)
import OffersInbox from "@/pages/OffersInbox";
import LandingPage from "@/pages/LandingPage";
import NativeWelcome from "@/pages/NativeWelcome";
import { isNativeApp } from "@/lib/nativeBridge";
import BuyerPreferences from "@/pages/BuyerPreferences";
import TrainerLearning from "@/pages/TrainerLearning";
import CourseCatalog from "@/pages/CourseCatalog";
import CourseDetail from "@/pages/CourseDetail";
import MySessions from "@/pages/MySessions";
import SessionJoin from "@/pages/SessionJoin";
import RegistrationPricing from "@/pages/RegistrationPricing";
import Dmca from "@/pages/Dmca";
import AdminGrowth from "@/pages/AdminGrowth";
import AdminGrowthPlaybook from "@/pages/AdminGrowthPlaybook";
import AdminFeatureMatrix from "@/pages/AdminFeatureMatrix";
import MyReferrals from "@/pages/MyReferrals";

// Boot Sentry once at module load — no-op when DSN env var is blank.
initSentry();

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] text-[#6B6B6B]">
        Loading…
      </div>
    );
  // Authed users go straight to their landing surface — admins to
  // /admin (operations summary), everyone else to /dashboard
  // (kennel/buyer dashboard). Unauthed visitors see the public
  // marketing landing page so cold traffic + SEO crawlers get
  // something useful instead of a forced login screen.
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  // Native app: app-like welcome screen instead of the web marketing
  // landing (App Review 4.2 first-launch experience).
  if (isNativeApp()) return <NativeWelcome />;
  return <LandingPage />;
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
          {/* Site-wide SEO defaults — kept minimal. Pages that ship
              their own <Helmet> (Journal, JournalPost, LandingPage,
              marketing reels, etc.) own their og:* / twitter:* /
              canonical fully. Adding them here too would force every
              such page into a deduplication race in <head> because
              react-helmet-async only dedupes within a single Helmet
              instance, not across the tree. So this top-level Helmet
              only ships the `<title>` default + meta description for
              the long tail of pages that don't bother. */}
          <Helmet defaultTitle="Armstrong K9 Registry">
            <meta name="description" content="Armstrong K9 Registry — pedigree-verified registration, marketplace, and breeding directory for exotic and purebred dogs. Trusted by serious breeders and discerning buyers." />
          </Helmet>
          <TopProgressBar />
          <ScrollToTop />
          <NativePushBootstrap />
          <InstallPrompt />
          <Toaster position="top-right" richColors />
          <ReferralCapture />
          <CookieConsent />
          <OfflineBanner />
          <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/sitter/:token" element={<SitterView />} />
          <Route path="/dog-log" element={<DogLogHub />} />
          <Route path="/dog-log/about" element={<DogLogLanding />} />
          <Route path="/armstrong" element={<Armstrong />} />
          <Route path="/armstrong/return" element={<Armstrong />} />
          <Route path="/armstrong/custom/:orderId" element={<ArmstrongCustomOrder />} />
          <Route path="/admin/armstrong" element={<AdminArmstrong />} />
          <Route path="/admin/storefronts" element={<ProtectedRoute><AdminStorefronts /></ProtectedRoute>} />
          <Route path="/me/store" element={<ProtectedRoute><MyStore /></ProtectedRoute>} />
          <Route path="/store/:slug" element={<PublicStore />} />
          <Route path="/store/:slug/custom/:orderId" element={<StoreCustomOrder />} />
          <Route path="/storefronts" element={<PublicStorefronts />} />
          <Route path="/about" element={<About />} />
          <Route path="/media" element={<MediaLibrary />} />
          <Route path="/media/:slug" element={<MediaItem />} />
          <Route path="/video-tours" element={<VideoToursLanding />} />
          <Route path="/services" element={<Services />} />
          <Route path="/marketing/reel/buyer" element={<MarketingReelBuyer />} />
          <Route path="/marketing/reel/seller" element={<MarketingReelSeller />} />
          <Route path="/verify-reference/:token" element={<VerifyReference />} />

          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dogs" element={<ProtectedRoute><Dogs /></ProtectedRoute>} />
          <Route path="/dogs/new" element={<ProtectedRoute><DogForm /></ProtectedRoute>} />
          <Route path="/dogs/:id" element={<ProtectedRoute><DogDetail /></ProtectedRoute>} />
          <Route path="/dogs/:id/edit" element={<ProtectedRoute><DogForm /></ProtectedRoute>} />
          <Route path="/dogs/:id/passport" element={<ProtectedRoute><CarePassport /></ProtectedRoute>} />

          <Route path="/litters" element={<ProtectedRoute><Litters /></ProtectedRoute>} />
          <Route path="/litters/new" element={<ProtectedRoute><LitterForm /></ProtectedRoute>} />
          <Route path="/litters/upcoming/new" element={<ProtectedRoute><UpcomingLitterNew /></ProtectedRoute>} />
          <Route path="/litters/upcoming/:id" element={<ProtectedRoute><UpcomingLitterDetail /></ProtectedRoute>} />
          <Route path="/litters/:litterId/confirmation" element={<ProtectedRoute><LitterConfirmation /></ProtectedRoute>} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/marketplace/wtb" element={<WtbBoard />} />
          <Route path="/marketplace/wtb/:id" element={<WtbDetail />} />
          <Route path="/intl-breeders" element={<IntlBreeders />} />
          <Route path="/intl-breeders/apply" element={<ProtectedRoute><IntlBreederApply /></ProtectedRoute>} />
          <Route path="/intl-breeders/me" element={<ProtectedRoute><IntlBreederApply /></ProtectedRoute>} />
          <Route path="/intl-breeders/:id" element={<IntlBreederDetail />} />
          <Route path="/import/:breedSlug/from/:country" element={<ImportBreedFromCountry />} />
          <Route path="/wtb/:breedSlug" element={<WtbByBreed />} />
          <Route path="/gift" element={<GiftPage />} />
          <Route path="/gift/sent" element={<GiftSentPage />} />
          <Route path="/gift/redeem/:code" element={<GiftRedeem />} />
          <Route path="/gift/redeem" element={<GiftRedeem />} />
          <Route path="/pricing/specialty" element={<PricingSpecialty />} />
          <Route path="/me/inquiries/:leadId/accept" element={<ProtectedRoute><AcceptInquiry /></ProtectedRoute>} />
          <Route path="/dashboard/intros" element={<ProtectedRoute><BuyerActivityFeed /></ProtectedRoute>} />
          <Route path="/marketplace/upcoming" element={<MarketplaceUpcoming />} />
          <Route path="/marketplace/upcoming/:id" element={<MarketplaceUpcomingDetail />} />
          {/* Programmatic SEO landing pages — `/puppies/{breed-slug}/{state-slug}` */}
          <Route path="/puppies/:breedSlug/:stateSlug" element={<PuppiesByBreedState />} />
          {/* Programmatic SEO landing pages — `/find-pros/cleared/{state-slug}` */}
          <Route path="/find-pros/cleared/:stateSlug" element={<ClearedProsByState />} />
          <Route path="/me/saved-searches" element={<ProtectedRoute><SavedSearches /></ProtectedRoute>} />
          <Route path="/catalog" element={<PublicCatalog />} />
          <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
          <Route path="/admin/pitch-kit" element={<ProtectedRoute><AdminPitchKit /></ProtectedRoute>} />
          <Route path="/breeds" element={<ProtectedRoute><BreedsCatalog /></ProtectedRoute>} />
          <Route path="/admin/breeds" element={<ProtectedRoute><AdminBreedsCatalog /></ProtectedRoute>} />

          <Route path="/breeders" element={<ProtectedRoute><Breeders /></ProtectedRoute>} />
          <Route path="/breeders/:id" element={<ProtectedRoute><BreederDetail /></ProtectedRoute>} />
          <Route path="/me/kennel" element={<ProtectedRoute><MyKennelRedirect /></ProtectedRoute>} />

          <Route path="/pedigree" element={<ProtectedRoute><Pedigree /></ProtectedRoute>} />
          <Route path="/pedigree/:id" element={<ProtectedRoute><Pedigree /></ProtectedRoute>} />
          {/* In-app PDF viewer — guarantees a visible Back button on
              iOS PWAs where the system "Done" overlay is suppressed. */}
          <Route path="/pdf-viewer" element={<ProtectedRoute><PdfViewer /></ProtectedRoute>} />

          <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />

          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/billing/return" element={<ProtectedRoute><BillingReturn /></ProtectedRoute>} />
          <Route path="/payouts" element={<ProtectedRoute><Payouts /></ProtectedRoute>} />
          <Route path="/escrow/my" element={<ProtectedRoute><MyEscrow /></ProtectedRoute>} />
          <Route path="/escrow/return" element={<ProtectedRoute><EscrowReturn /></ProtectedRoute>} />
          <Route path="/admin/escrow" element={<ProtectedRoute><AdminEscrow /></ProtectedRoute>} />
          <Route path="/import/dogs" element={<ProtectedRoute><ImportDogs /></ProtectedRoute>} />
          <Route path="/breeder/analytics" element={<ProtectedRoute><BreederAnalytics /></ProtectedRoute>} />
          <Route path="/me/referral" element={<ProtectedRoute><ReferralProgram /></ProtectedRoute>} />
          <Route path="/me/buyer-preferences" element={<ProtectedRoute><BuyerPreferences /></ProtectedRoute>} />
          <Route path="/chat" element={<Navigate to="/messages" replace />} />
          <Route path="/chat/:threadId" element={<Navigate to="/messages" replace />} />
          <Route path="/offers/my" element={<ProtectedRoute><OffersInbox /></ProtectedRoute>} />
          <Route path="/certificates" element={<ProtectedRoute><MyCertificates /></ProtectedRoute>} />
          <Route path="/breeding" element={<ProtectedRoute><Breeding /></ProtectedRoute>} />
          <Route path="/breeding/my" element={<ProtectedRoute><MyBreedingListings /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/settings/privacy" element={<ProtectedRoute><PrivacyDataPage /></ProtectedRoute>} />
          {/* Account-deletion URL declared in Google Play Data safety form */}
          <Route path="/account/delete" element={<Navigate to="/settings/privacy" replace />} />
          <Route path="/onboarding/bulk-import" element={<ProtectedRoute><BulkImportDogs /></ProtectedRoute>} />
          <Route path="/share-kennel" element={<ProtectedRoute><ShareKennelPage /></ProtectedRoute>} />
          <Route path="/contracts" element={<ProtectedRoute><ContractsHub /></ProtectedRoute>} />
          <Route path="/admin/dogs/reassign" element={<ProtectedRoute><AdminDogReassign /></ProtectedRoute>} />
          <Route path="/admin/storefronts/reassign" element={<ProtectedRoute><AdminStorefrontReassign /></ProtectedRoute>} />
          <Route path="/admin/armstrong/migrate" element={<ProtectedRoute><AdminArmstrongMigrate /></ProtectedRoute>} />
          <Route path="/admin/storefronts/health" element={<ProtectedRoute><AdminStorefrontsHealth /></ProtectedRoute>} />
          <Route path="/admin/contracts" element={<ProtectedRoute><AdminContracts /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="/admin/promo-codes" element={<ProtectedRoute><AdminPromoCodes /></ProtectedRoute>} />
          <Route path="/admin/comp-list" element={<ProtectedRoute><AdminCompList /></ProtectedRoute>} />
          <Route path="/admin/docs/:key" element={<ProtectedRoute><AdminDocViewer /></ProtectedRoute>} />
          <Route path="/admin/providers" element={<ProtectedRoute><AdminServiceProviders /></ProtectedRoute>} />
          <Route path="/provider/me" element={<ProtectedRoute><ProviderOnboarding /></ProtectedRoute>} />
          {/* `/become-a-provider` is the CTA target sprinkled across the
              landing page, pricing page, persona dashboards, billing
              page, and the admin-invite email template. It is the same
              ProviderOnboarding wizard as `/provider/me` — ProviderOnboarding
              auto-switches between "first-time apply" and "edit existing
              profile" based on whether `/api/providers/mine` returns a
              row, so one component handles both surfaces. Without this
              line every "Become a provider" CTA on the site (including
              admin invite emails) 404'd, which is what Lauren caught
              trying to sign her husband up as a trainer on 2026-06-05. */}
          <Route path="/become-a-provider" element={<ProtectedRoute><ProviderOnboarding /></ProtectedRoute>} />
          {/* Provider ↔ Client direct messaging — see routers/provider_messages.py.
              Both pages share the same authed shell; ThreadView auto-detects
              the caller's side (provider vs client) from the thread row, so we
              don't fork the routing tree. */}
          <Route path="/inbox/threads" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/inbox/threads/:id" element={<ProtectedRoute><ThreadView /></ProtectedRoute>} />
          <Route path="/find-pros" element={<FindPros />} />
          <Route path="/provider/:slug" element={<PublicProvider />} />
          <Route path="/my-bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
          <Route path="/transfers" element={<ProtectedRoute><Transfers /></ProtectedRoute>} />
          {/* Per-transfer pay landing page reached from the email invite —
              public so an unregistered buyer can preview before sign-up. */}
          <Route path="/transfers/pay/:id" element={<TransferPay />} />
          <Route path="/admin/transfers" element={<ProtectedRoute><AdminTransfers /></ProtectedRoute>} />
          <Route path="/admin/approvals" element={<ProtectedRoute><AdminApprovals /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
          <Route path="/admin/dna-queue" element={<ProtectedRoute><AdminDNAQueue /></ProtectedRoute>} />
          <Route path="/dna-verified" element={<WhyDNAVerified />} />
          <Route path="/admin/pedigree-additions" element={<ProtectedRoute><AdminPedigreeAdditions /></ProtectedRoute>} />
          <Route path="/admin/master-pedigree" element={<ProtectedRoute><AdminMasterPedigree /></ProtectedRoute>} />
          <Route path="/admin/data" element={<ProtectedRoute><AdminData /></ProtectedRoute>} />
          {/* Dedicated discoverable URL for user management — mounts AdminData
              defaulted to the Users tab. /admin/data?tab=users still works too. */}
          <Route path="/admin/users" element={<ProtectedRoute><AdminData defaultTab="users" /></ProtectedRoute>} />
          <Route path="/admin/about-photos" element={<ProtectedRoute><AdminAboutPhotos /></ProtectedRoute>} />
          <Route path="/admin/journal" element={<ProtectedRoute><AdminJournal /></ProtectedRoute>} />
          <Route path="/admin/journal/settings" element={<ProtectedRoute><AdminJournalSettings /></ProtectedRoute>} />
          <Route path="/admin/puppy-school" element={<ProtectedRoute><AdminPuppySchool /></ProtectedRoute>} />
          <Route path="/admin/ip-portfolio" element={<ProtectedRoute><AdminIPPortfolio /></ProtectedRoute>} />

          {/* Public events (no auth required) */}
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />

          {/* Public breeder profile (no auth required, shareable) */}
          <Route path="/k/:id" element={<PublicBreeder />} />
          {/* Public kennels directory (no auth required) */}
          <Route path="/kennels" element={<PublicKennels />} />
          <Route path="/verified-seal" element={<VerifiedSeal />} />
          {/* Editorial journal (no auth required) */}
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<JournalPost />} />
          {/* Public dog profile (no auth required, shareable) */}
          <Route path="/d/:id" element={<PublicDog />} />

          {/* Public legal / informational pages (no auth required) */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/legal/worldwide-drafts" element={<WorldwideLegalDrafts />} />
          <Route path="/storefront-seller-agreement" element={<StorefrontSellerAgreement />} />
          <Route path="/consent" element={<UserConsent />} />
          <Route path="/ethics" element={<EthicalConsiderations />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/security" element={<SecurityMeasures />} />
          <Route path="/compliance" element={<ComplianceDisclosures />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/get-app" element={<Navigate to="/install" replace />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/sub-processors" element={<SubProcessors />} />
          <Route path="/cookies" element={<CookieNotice />} />
          <Route path="/ai-use" element={<AIUseDisclosure />} />
          <Route path="/auto-renew" element={<AutoRenewDisclosure />} />
          <Route path="/content-license" element={<PhotoContentLicense />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/one-pagers" element={<OnePagers />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/seller-standards" element={<SellerStandards />} />
          <Route path="/seller-standards/attest" element={<ProtectedRoute><SellerStandardsAttest /></ProtectedRoute>} />
          <Route path="/admin/seller-standards" element={<ProtectedRoute><AdminSellerStandards /></ProtectedRoute>} />
          <Route path="/buyer-rights" element={<BuyerRights />} />
          <Route path="/admin/buyer-rights" element={<ProtectedRoute><AdminBuyerRights /></ProtectedRoute>} />
          <Route path="/admin/legal-bundle" element={<ProtectedRoute><AdminLegalBundle /></ProtectedRoute>} />
          <Route path="/admin/launch-readiness" element={<ProtectedRoute><AdminLaunchReadiness /></ProtectedRoute>} />
          <Route path="/admin/press-kit" element={<ProtectedRoute><PressKit /></ProtectedRoute>} />
          <Route path="/admin/stripe-radar" element={<ProtectedRoute><AdminStripeRadar /></ProtectedRoute>} />
          <Route path="/admin/stripe-pivot-playbook" element={<ProtectedRoute><AdminStripePivotPlaybook /></ProtectedRoute>} />
          <Route path="/admin/roadmap" element={<ProtectedRoute><AdminRoadmap /></ProtectedRoute>} />
          <Route path="/admin/launch-pack" element={<ProtectedRoute><AdminLaunchPack /></ProtectedRoute>} />
          <Route path="/admin/launch-playbook" element={<ProtectedRoute><AdminLaunchPlaybook /></ProtectedRoute>} />
          <Route path="/admin/mobile-launch" element={<ProtectedRoute><AdminMobileLaunch /></ProtectedRoute>} />
          <Route path="/account/subscription" element={<ProtectedRoute><AccountSubscription /></ProtectedRoute>} />
          <Route path="/admin/paywall-audit" element={<ProtectedRoute><AdminPaywallAudit /></ProtectedRoute>} />
          <Route path="/admin/announcements" element={<ProtectedRoute><AdminAnnouncements /></ProtectedRoute>} />
          <Route path="/admin/connect-diagnose" element={<ProtectedRoute><AdminConnectDiagnose /></ProtectedRoute>} />
          <Route path="/tours" element={<ProtectedRoute><MyVideoTours /></ProtectedRoute>} />
          <Route path="/tours/:id/room" element={<ProtectedRoute><VideoTourRoom /></ProtectedRoute>} />
          <Route path="/admin/marketing" element={<ProtectedRoute><AdminMarketing /></ProtectedRoute>} />
          <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
          <Route path="/admin/messaging" element={<ProtectedRoute><AdminMessaging /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminOperations /></ProtectedRoute>} />
          <Route path="/admin/operations" element={<ProtectedRoute><AdminOperations /></ProtectedRoute>} />
          <Route path="/admin/compliance" element={<ProtectedRoute><AdminCompliance /></ProtectedRoute>} />
          <Route path="/admin/console" element={<ProtectedRoute><AdminConsole /></ProtectedRoute>} />
          <Route path="/admin/security" element={<ProtectedRoute><AdminSecurity /></ProtectedRoute>} />
          <Route path="/admin/business-classification" element={<ProtectedRoute><AdminBusinessClassification /></ProtectedRoute>} />
          <Route path="/admin/operator-binder" element={<ProtectedRoute><AdminOperatorBinder /></ProtectedRoute>} />
          <Route path="/admin/transporters" element={<ProtectedRoute><AdminTransporters /></ProtectedRoute>} />
          <Route path="/admin/transport-disputes" element={<ProtectedRoute><AdminTransportDisputes /></ProtectedRoute>} />
          <Route path="/admin/personas" element={<ProtectedRoute><AdminPersonas /></ProtectedRoute>} />
          <Route path="/admin/pricing" element={<ProtectedRoute><AdminPricing /></ProtectedRoute>} />
          <Route path="/admin/pricing-reference" element={<ProtectedRoute><AdminPricingReference /></ProtectedRoute>} />
          <Route path="/admin/intl-ops" element={<ProtectedRoute><AdminIntlOps /></ProtectedRoute>} />
          <Route path="/admin/intl-couriers" element={<ProtectedRoute><AdminIntlCouriers /></ProtectedRoute>} />
          <Route path="/admin/chat-reports" element={<ProtectedRoute><AdminChatReports /></ProtectedRoute>} />
          <Route path="/admin/users-ops" element={<ProtectedRoute><AdminUsersOps /></ProtectedRoute>} />
          <Route path="/admin/share-invite" element={<ProtectedRoute><AdminShareInvite /></ProtectedRoute>} />
          <Route path="/invite" element={<ProtectedRoute><InviteFriends /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/admin/intl-sellers" element={<ProtectedRoute><AdminIntlSellers /></ProtectedRoute>} />
          <Route path="/admin/feed-prompts" element={<ProtectedRoute><AdminFeedPrompts /></ProtectedRoute>} />
          <Route path="/admin/onboarding" element={<ProtectedRoute><AdminOnboarding /></ProtectedRoute>} />
          <Route path="/admin/invites" element={<ProtectedRoute><AdminInvites /></ProtectedRoute>} />
          <Route path="/admin/marketing/refresh" element={<ProtectedRoute><AdminMarketingRefresh /></ProtectedRoute>} />
          <Route path="/admin/social-bios" element={<ProtectedRoute><AdminSocialBios /></ProtectedRoute>} />
          <Route path="/admin/checkr-setup" element={<ProtectedRoute><AdminCheckrSetup /></ProtectedRoute>} />
          <Route path="/admin/stripe-tax-setup" element={<ProtectedRoute><AdminStripeTaxSetup /></ProtectedRoute>} />
          <Route path="/help/stripe-tax-setup" element={<ProtectedRoute><StripeTaxSetupHelp /></ProtectedRoute>} />
          <Route path="/help/1099-k-and-taxes" element={<ProtectedRoute><HelpTaxes1099k /></ProtectedRoute>} />
          <Route path="/connect/return" element={<ProtectedRoute><StripeConnectReturn /></ProtectedRoute>} />
          {/* Install — bookmarkable browser-aware home-screen install page.
              Public (no auth) so people can share the link to friends or
              load it pre-signup; also the destination for QR codes. */}
          <Route path="/install" element={<Install />} />
          <Route path="/transport" element={<TransportDirectory />} />
          <Route path="/transport/route/:from/:to" element={<TransportRoutePage />} />
          <Route path="/transport/profile/:slug" element={<TransportProfile />} />
          <Route path="/transport/getting-started" element={<TransportGettingStarted />} />
          <Route path="/transport/request" element={<TransportRequest />} />
          <Route path="/transport/apply" element={<TransportApply />} />
          <Route path="/transport/international" element={<InternationalTransportDirectory />} />
          <Route path="/transport/international/apply" element={<InternationalTransportApply />} />
          <Route path="/transport/international/apply/thanks" element={<InternationalTransportApplyThanks />} />
          <Route path="/transport/international/:slug" element={<InternationalTransporterProfile />} />
          <Route path="/services/international" element={<InternationalServicesDirectory />} />
          <Route path="/services/international/apply" element={<InternationalServiceProApply />} />
          <Route path="/services/international/:slug" element={<InternationalServiceProProfile />} />
          <Route path="/trip/:token" element={<TripPublic />} />
          <Route path="/transport/orders/:order_id" element={<TransportOrderDetail />} />
          <Route path="/transporter" element={<ProtectedRoute><TransporterDashboard /></ProtectedRoute>} />

          <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/accounting" element={<ProtectedRoute><AdminAccounting /></ProtectedRoute>} />
          <Route path="/admin/documents" element={<ProtectedRoute><AdminDocuments /></ProtectedRoute>} />
          <Route path="/admin/reset" element={<ProtectedRoute><AdminReset /></ProtectedRoute>} />
          <Route path="/admin/duplicates" element={<ProtectedRoute><AdminDuplicateDogs /></ProtectedRoute>} />
          <Route path="/admin/push-test" element={<ProtectedRoute><AdminPushTest /></ProtectedRoute>} />
          <Route path="/p/:postId" element={<PublicPost />} />
          <Route path="/admin/automations" element={<ProtectedRoute><AdminAutomations /></ProtectedRoute>} />
          <Route path="/admin/referrals" element={<ProtectedRoute><AdminReferrals /></ProtectedRoute>} />
          <Route path="/admin/events" element={<ProtectedRoute><AdminEvents /></ProtectedRoute>} />
          <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />

          {/* Trainer Learning — paid 1-on-1 live video sessions
              (Daily.co) and pre-recorded video courses. Trainer-side
              configuration + course CRUD lives at `/me/trainer-learning`,
              the public buyer-facing catalog at `/courses`, individual
              course detail/watch page at `/courses/:slug`, and the
              buyer's booked sessions at `/me/sessions`. The live-room
              page at `/sessions/:id/join` is shared by buyer + trainer
              and gates the Daily.co iframe behind a per-user meeting
              token returned by the backend `join` endpoint. */}
          <Route path="/me/trainer-learning" element={<ProtectedRoute><TrainerLearning /></ProtectedRoute>} />
          <Route path="/courses" element={<CourseCatalog />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/me/sessions" element={<ProtectedRoute><MySessions /></ProtectedRoute>} />
          <Route path="/sessions/:id/join" element={<ProtectedRoute><SessionJoin /></ProtectedRoute>} />

          {/* Public 1-page explainer of what the $25 dog-registration
              fee actually unlocks. Targets cold ad/SEO traffic that
              isn't ready for the full /pricing comparison grid. */}
          <Route path="/registration" element={<RegistrationPricing />} />

          {/* DMCA takedown notice form. Public, no auth — required by
              17 USC §512(c)(3) safe-harbor. Submissions POST to
              `/api/dmca/notice`; admin queue lives at /admin/dmca. */}
          <Route path="/dmca" element={<Dmca />} />

          {/* Admin growth dashboard + the saved growth playbook (the
              opinionated prose advice that drove the dashboard build).
              Both are protected by admin-role checks inside the page. */}
          <Route path="/admin/growth" element={<ProtectedRoute><AdminGrowth /></ProtectedRoute>} />
          <Route path="/admin/growth/playbook" element={<ProtectedRoute><AdminGrowthPlaybook /></ProtectedRoute>} />
          <Route path="/admin/feature-matrix" element={<ProtectedRoute><AdminFeatureMatrix /></ProtectedRoute>} />
          <Route path="/me/referrals" element={<ProtectedRoute><MyReferrals /></ProtectedRoute>} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
